import { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import { Draw, Modify } from 'ol/interaction.js';
import type { DrawEvent } from 'ol/interaction/Draw';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
} from 'ol/style';
import { ref } from 'vue';

import { DrawToolType } from '@nora-monorepo/nora-api-client';
import { isNonEmptyString } from '@nora-monorepo/shared';

import { CreateFeatureBaseDrawTool } from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import useSymbolToolStore from '#nora-ui/map/drawtools/stores/symbol-tool-store';
import { getHandleStyle } from '#nora-ui/map/drawtools/styles/circle-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { SymbolToolOptions } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool.types';
import { SymbolCompletionBehavior } from '#nora-ui/map/drawtools/symbol-tool/symbol-tool.types';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { FeatureStyleProperties } from '#nora-ui/map/feature-properties';
import { getFeatureProperties } from '#nora-ui/map/feature-properties';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

export class SymbolTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.symbol;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
  };

  private readonly modifyInteraction: Modify;
  private readonly handleSource: VectorSource;
  private readonly handleLayer: VectorLayer<VectorSource>;

  private handle!: Feature<Point>;

  private readonly drawInteraction: Draw;
  private selectedSymbol?: SymbolToolOptions;
  private readonly selectedPackTintable = ref(false);
  private completionBehavior = SymbolCompletionBehavior.ReopenSymbolPicker;
  private hasManualFeatureNameOverride = false;

  symbolToolStore = useSymbolToolStore();

  constructor(drawToolManager: DrawToolManager, options?: SymbolToolOptions) {
    super(drawToolManager);
    this.drawToolName = 'Symbol';
    const invisibleDrawStyle = new Style({
      image: new CircleStyle({
        radius: 0,
        fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
        stroke: new Stroke({ color: 'rgba(0, 0, 0, 0)' }),
      }),
      fill: new Fill({ color: 'rgba(0, 0, 0, 0)' }),
      stroke: new Stroke({ color: 'rgba(0, 0, 0, 0)' }),
    });
    this.drawInteraction = new Draw({
      source: this.editingVectorSource,
      type: 'Point',
      style: invisibleDrawStyle,
    });
    this.editingVectorLayer.setStyle(getFeatureStyle);
    this.drawInteraction.on('drawend', this.onDrawEnd.bind(this));

    if (options) {
      if ('symbolPackId' in options) {
        const selectedSymbol = {
          symbolId: options.symbolId,
          symbolPackId: options.symbolPackId,
          symbolName: options.symbolName,
        };
        this.completionBehavior = options.completionBehavior ?? SymbolCompletionBehavior.ReopenSymbolPicker;
        this.setSelectedSymbol(selectedSymbol);
        this.symbolToolStore.setSelectedSymbol(selectedSymbol);
      }
      else {
        this.setSelectedSymbol({
          fontIcon: options.fontIcon,
        });
      }
    }

    this.handleSource = new VectorSource();
    this.handleLayer = new VectorLayer<VectorSource>({
      source: this.handleSource,
      style: getFeatureStyle,
      zIndex: 10000,
    });

    this.modifyInteraction = new Modify({
      source: this.handleSource,
      style: getHandleStyle,
      insertVertexCondition: () => false,
      pixelTolerance: getHandleModifyPixelTolerance(),
    });

    this.modifyInteraction.on('modifystart', (event) => {
      const features = event.features.getArray();

      if (features.length > 0) {
        this.updateSymbolTransform();
      }
    });

    this.modifyInteraction.on('modifyend', () => {
      if (!this.feature) {
        return;
      }
      this.feature.changed();
      this.markDirty();
    });
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addInteraction(this.drawInteraction);
    map.addLayer(this.handleLayer);
    map.addInteraction(this.modifyInteraction);
    if (this.feature) {
      this.addHandle(this.feature);
    }
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.drawInteraction);
    map.removeInteraction(this.modifyInteraction);
    this.handleSource.clear();
    this.handleLayer.setVisible(false);
    map.removeLayer(this.handleLayer);
    this.selectedSymbol = undefined;
    this.selectedPackTintable.value = false;
    this.completionBehavior = SymbolCompletionBehavior.ReopenSymbolPicker;

    this.symbolToolStore.reset();
  }

  handleSymbolToolCompletionBehaviour(completionBehavior: SymbolCompletionBehavior) {
    // After-save or dismiss behavior depends on how symbol selection was started.
    switch (completionBehavior) {
      // Favourite symbol from ToolListView: return to tool list.
      case SymbolCompletionBehavior.ReopenToolList:
        this.drawToolManager.map.drawToolUI.openToolList();
        return;

      // Context-menu/quick action: close without reopening picker or tool list.
      // TODO: should in addition auto-save the symbol on the map, without the comfirm dialog
      case SymbolCompletionBehavior.Close:
        this.drawToolManager.map.drawToolUI.setDrawToolByType(null);
        return;

      // Regular symbol picker flow: reopen picker for another placement.
      case SymbolCompletionBehavior.ReopenSymbolPicker:
      default:
        this.drawToolManager.map.drawToolUI.setDrawToolByType(this.drawToolType);
    }
  }

  setCompletionBehavior(completionBehavior: SymbolCompletionBehavior): void {
    this.completionBehavior = completionBehavior;
  }

  override async save(feature?: Feature<Geometry> | null): Promise<void> {
    const completionBehavior = this.completionBehavior;

    await super.save(feature);

    if (this.drawToolManager.map.drawToolUI.activeDrawTool.value !== this) {
      return;
    }

    this.handleSymbolToolCompletionBehaviour(completionBehavior);
  }

  override dismiss(): void {
    const drawToolUI = this.drawToolManager.map.drawToolUI;

    // Only re-open from explicit dismiss on the active tool (i.e. toolbar discard). Select-clone
    // dismissals from the map and the post-save dismiss on the replaced instance must be no-ops.
    if (drawToolUI.activeDrawTool.value !== this) {
      return;
    }

    this.handleSymbolToolCompletionBehaviour(this.completionBehavior);
  }

  private addHandle(pointFeature: Feature<Geometry>): void {
    const geometry = pointFeature.getGeometry();
    if (!(geometry instanceof Point)) {
      return;
    }

    this.handle = new Feature<Point>(new Point(geometry.getCoordinates()));
    this.handle.setStyle(getHandleStyle);
    this.handle.set('type', 'center-handle');
    this.handleSource.clear();
    this.handleSource.addFeature(this.handle);
    this.handleLayer.setVisible(true);

    const handleGeometry = this.handle.getGeometry() as Point;
    handleGeometry.on('change', () => {
      this.updateSymbolTransform();
    });
  }

  private updateSymbolTransform(): void {
    if (!this.feature) {
      return;
    }
    const handleGeometry = this.handle.getGeometry() as Point;
    const newCoordinates = handleGeometry.getCoordinates();
    this.feature.setGeometry(new Point(newCoordinates));
    this.feature.changed();
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    const geometry = feature.getGeometry();

    if (!(geometry instanceof Point)) {
      return false;
    }

    const { id, symbolPackId, symbolId, fontIcon } = getFeatureProperties<FeatureStyleProperties>(feature);
    this.featureId = id;
    const coordinates = geometry.getCoordinates() as [number, number];

    const featureProperties = extractDrawToolOptions(feature);
    this.drawToolManager.updateDrawStyleOptions(featureProperties);

    this.feature = new Feature<Point>(new Point(coordinates));
    this.feature.setProperties({
      ...this.getDefaultProperties(),
      ...feature.getProperties(),
      ...featureProperties,
      id: this.featureId,
    });
    this.feature.setStyle(getFeatureStyle);

    if (isNonEmptyString(symbolPackId) && isNonEmptyString(symbolId)) {
      this.setSelectedSymbol({ symbolPackId, symbolId });
      this.symbolToolStore.setSelectedSymbol({ symbolPackId, symbolId });
    }
    else if (isNonEmptyString(fontIcon)) {
      this.setSelectedSymbol({ fontIcon });
    }

    super.loadFeature(this.feature);

    this.addHandle(this.feature);

    return true;
  }

  setSelectedSymbol(symbol: SymbolToolOptions): void {
    this.selectedSymbol = symbol;
    this.refreshSelectedPackTintable(symbol).catch((error: unknown) => {
      console.warn('Failed to load pack tintable flag', error);
    });
  }

  private async refreshSelectedPackTintable(symbol: SymbolToolOptions): Promise<void> {
    if (!('symbolPackId' in symbol)) {
      this.selectedPackTintable.value = false;

      return;
    }
    const pack = await this.symbolToolStore.loadSymbolPack(symbol.symbolPackId);

    this.selectedPackTintable.value = pack?.tintable ?? false;
  }

  hasSelectedSymbol(): boolean {
    return this.selectedSymbol !== undefined;
  }

  // Lets us display a symbol name placeholder text, if present, on the label input field
  override getLabelPlaceholder(): string {
    if (!this.selectedSymbol || !('symbolPackId' in this.selectedSymbol)) {
      return '';
    }

    return this.selectedSymbol.symbolName ?? '';
  }

  override setFeatureName(name: string): void {
    this.hasManualFeatureNameOverride = true;
    super.setFeatureName(name);
  }

  /**
   * Set the feature label to the symbol name when needed.
   * An example when it is not needed is if the user already has set a label, and we do not want to override their label.
   */
  private applyDefaultSymbolLabelIfNeeded(): void {
    if (!this.selectedSymbol) {
      return;
    }

    const isFirstPlacement = !this.feature;
    if (
      isFirstPlacement
      && !this.hasManualFeatureNameOverride
      && 'symbolPackId' in this.selectedSymbol
      && this.selectedSymbol.symbolName !== undefined
      && this.selectedSymbol.symbolName !== ''
    ) {
      this.featureName = this.selectedSymbol.symbolName;
    }
  }

  onDrawEnd(event: DrawEvent): void {
    this.editingVectorSource.clear();
    if (!this.selectedSymbol) {
      return;
    }

    this.applyDefaultSymbolLabelIfNeeded();

    const coordinate = (event.feature.getGeometry() as Point).getCoordinates();

    const existingFeatureId: string | undefined = this.feature?.get('id') as string;

    if (this.feature && existingFeatureId) {
      this.feature.setGeometry(new Point(coordinate));
      this.addHandle(this.feature);
    }
    else {
      this.feature = new Feature({
        geometry: new Point(coordinate),
      });
    }

    if ('symbolPackId' in this.selectedSymbol) {
      this.feature.set('symbolPackId', this.selectedSymbol.symbolPackId);
      this.feature.set('symbolId', this.selectedSymbol.symbolId);
    }
    else {
      this.feature.set('fontIcon', this.selectedSymbol.fontIcon);
    }

    this.feature.setProperties(this.getDefaultProperties());
    this.feature.setStyle(getFeatureStyle);
    this.editingVectorSource.addFeature(this.feature);
    this.markDirty();
  }

  override showStyles(): boolean {
    return true;
  }

  override showFillColor(): boolean {
    return this.selectedPackTintable.value;
  }

  override showFillStyle(): boolean {
    return false;
  }

  override showLineColor(): boolean {
    return false;
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    this.feature?.setStyle(getFeatureStyle);
    this.markDirty();
  }

  override getBoundingBoxBuffer(): number {
    const iconSize = 24;
    const map = this.drawToolManager.map.getOlMapOrThrow();
    const resolution = map.getView().getResolution() ?? 1;
    return (iconSize / 2) * resolution;
  }

  onCloseButtonPressed(): void {
    this.handleCloseTool();
  }

  handleCloseTool(): void {
    this.drawToolManager.map.drawToolUI.setDrawToolByType(null);
  }
}
